import React, { useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowLeftIcon, ArrowRightIcon } from "lucide-react";
import { Lightbox } from "../components/gallery/Lightbox";
import { MapleLeaf } from "../components/MapleLeaf";
import { WhatsAppLine } from "../components/WhatsAppLine";
import { RESIDENCES } from "../data/residences";
import type { ResidenceKey } from "../data/residences";
import type { GalleryItem } from "../data/gallery";
import { EASE } from "../utils/motion";
const SPANS_FIVE = ["md:col-span-7 md:row-span-2", "md:col-span-5", "md:col-span-5", "md:col-span-4", "md:col-span-8"];
const SPANS_FOUR = ["md:col-span-7 md:row-span-2", "md:col-span-5", "md:col-span-5", "md:col-span-12"];
function spanFor(i: number, count: number) {
  const spans = count === 4 ? SPANS_FOUR : SPANS_FIVE;
  return spans[i % spans.length];
}
export function ResidenceGallery() {
  const {
    residence
  } = useParams();
  const reduce = useReducedMotion();
  const [open, setOpen] = useState<number | null>(null);
  if (residence !== "girls" && residence !== "boys") return <Navigate to="/boarding" replace data-id="element-1515" data-mp="26vh8bujk4c88" />;
  const data = RESIDENCES[residence as ResidenceKey];
  const other = RESIDENCES[residence === "girls" ? "boys" : "girls"];
  const items: GalleryItem[] = data.photos.map((p, i) => ({
    id: `${data.key}-${i}`,
    src: p.src,
    alt: p.alt,
    caption: p.caption,
    album: "Boarding",
    shape: "square"
  }));
  return <>
      <main className="bg-[#fbfaf6] pt-24" data-id="element-1516" data-mp="34hwrr1vjnjyf">
        <div className="mx-auto flex max-w-[1440px] items-center justify-between px-5 py-4 md:px-9" data-id="element-1517" data-mp="2plyypsgx2vqf">
          <Link to="/boarding" className="group inline-flex items-center gap-2 border border-[#061f38] px-4 py-3 text-sm font-semibold transition-colors duration-200 hover:bg-[#061f38] hover:text-white" data-id="element-1518" data-mp="2ul1qwcbst7gz">
            <ArrowLeftIcon size={15} className="transition-transform duration-200 group-hover:-translate-x-1" data-id="element-1519" data-mp="1j5mwngaz4tpk" /> Back to Boarding
          </Link>
          <Link to={`/boarding/residences/${other.key}`} className="group hidden items-center gap-2 text-sm font-semibold transition-colors duration-200 hover:text-[#bb1e35] sm:inline-flex" data-id="element-1520" data-mp="3lr8s6prm2mj6">
            {other.name} <ArrowRightIcon size={15} className="transition-transform duration-200 group-hover:translate-x-1" data-id="element-1521" data-mp="3c15ysogkv6kb" />
          </Link>
        </div>

        <section key={data.key} className="mx-auto grid max-w-[1440px] gap-10 px-5 pb-12 pt-10 md:px-9 lg:grid-cols-12 lg:pt-16" data-id="element-1522" data-mp="324kuy6gao1i2">
          <motion.div initial={{
          opacity: 0,
          x: reduce ? 0 : -40
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: reduce ? 0.01 : 0.8,
          ease: EASE
        }} className="lg:col-span-7" data-id="element-1523" data-mp="2y2pravaw5c14">
            <p className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.18em] text-[#bb1e35]" data-id="element-1524" data-mp="2o5bc0z0petae"><MapleLeaf size={11} data-id="element-1525" data-mp="rd1ew263p5q0" /> BOARDING · RESIDENCE GALLERY</p>
            <h1 className="mt-6 text-[clamp(3rem,7vw,6.5rem)] font-medium leading-[0.9] tracking-[-0.075em]" data-id="element-1526" data-mp="ra5gydu96h1k">{data.name}</h1>
          </motion.div>
          <motion.div initial={{
          opacity: 0,
          x: reduce ? 0 : 40
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: reduce ? 0.01 : 0.8,
          delay: reduce ? 0 : 0.1,
          ease: EASE
        }} className="flex flex-col justify-end lg:col-span-4 lg:col-start-9" data-id="element-1527" data-mp="tvwhegoqs0im">
            <p className="text-lg leading-8 text-[#3d4b57]" data-id="element-1528" data-mp="13p3kfuw0f4fj">{data.intro}</p>
          </motion.div>
        </section>

        <section className="mx-auto max-w-[1440px] px-5 pb-24 md:px-9 md:pb-32" data-id="element-1529" data-mp="aybq72yi8puu">
          <ul className="grid auto-rows-[220px] grid-cols-1 gap-3 md:auto-rows-[260px] md:grid-cols-12" data-id="element-1530" data-mp="b0g66j9ohfbh">
            {data.photos.map((p, i) => <motion.li key={p.caption} initial={{
            opacity: 0,
            clipPath: reduce ? "inset(0 0 0 0)" : "inset(12% 12% 12% 12%)"
          }} whileInView={{
            opacity: 1,
            clipPath: "inset(0% 0% 0% 0%)"
          }} viewport={{
            once: true,
            amount: 0.25
          }} transition={{
            duration: reduce ? 0.01 : 0.9,
            delay: reduce ? 0 : i % 3 * 0.08,
            ease: EASE
          }} className={spanFor(i, data.photos.length)} data-id="element-1531" data-mp="kv809d41xk1i" data-mpi={i}>
                <button type="button" onClick={() => setOpen(i)} aria-label={`Open ${p.caption}`} className="group relative h-full w-full overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-[#bb1e35] focus-visible:ring-offset-2" data-id="element-1532" data-mp="1uur7p2pamf8z">
                  <img src={p.src} alt={p.alt} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" data-id="element-1533" data-mp="23x0iltoh4amw" />
                  <span className="absolute bottom-0 left-0 bg-[#fbfaf6] px-4 py-2 text-sm font-medium" data-id="element-1534" data-mp="2dsbzahjfzrxa">{p.caption}</span>
                </button>
              </motion.li>)}
          </ul>
          <div className="mt-10 flex flex-col justify-between gap-6 border-t border-[#dedbd3] pt-8 md:flex-row md:items-center" data-id="element-1535" data-mp="2mmqc8dgi2qd8">
            <p className="text-xs text-[#6b7378]" data-id="element-1536" data-mp="31glh78djbvj5">{data.photos.length} photos from the {data.short}. Select any photo to view it full size.</p>
            <WhatsAppLine label="Ask about boarding" context={`the ${data.name.toLowerCase()} and boarding availability`} data-id="element-1537" data-mp="3g4vb8zu3prne" />
          </div>
        </section>

        <section className="bg-[#061f38] text-white" data-id="element-1538" data-mp="3k4vx35jj554h">
          <Link to={`/boarding/residences/${other.key}`} className="group mx-auto grid max-w-[1440px] items-center gap-8 px-5 py-14 md:grid-cols-[1fr_auto] md:px-9" data-id="element-1539" data-mp="3665p2uv69l1c">
            <div data-id="element-1540" data-mp="2whfcyq6f4gom">
              <p className="text-xs font-semibold tracking-[0.18em] text-[#f0b1b9]" data-id="element-1541" data-mp="2wgw03nvmkihg">NEXT RESIDENCE</p>
              <p className="mt-3 text-4xl font-medium tracking-[-0.06em] transition-colors duration-200 group-hover:text-[#f0b1b9] md:text-6xl" data-id="element-1542" data-mp="318dyq5a6e6z9">{other.name}</p>
            </div>
            <span className="grid h-16 w-16 place-items-center bg-[#c5233d] transition-transform duration-200 group-hover:translate-x-1" data-id="element-1543" data-mp="2sfty4zabpsbs"><ArrowRightIcon size={22} data-id="element-1544" data-mp="2di9wo2cmyfad" /></span>
          </Link>
        </section>
      </main>

      <AnimatePresence data-id="element-1545" data-mp="2ii63088t2it3">
        {open !== null && <Lightbox items={items} index={open} onClose={() => setOpen(null)} onChange={setOpen} data-id="element-1546" data-mp="2iguvpyg5isx8" />}
      </AnimatePresence>
    </>;
}